import { Controller } from "@hotwired/stimulus";

const FIELD_TYPES = {
  Text: { type: "string", xFormat: null, hasEnum: false },
  "Date/Time": { type: "string", xFormat: "datetime", hasEnum: false },
  "Text Area": { type: "string", xFormat: "textarea", hasEnum: false },
  Dropdown: { type: "string", xFormat: null, hasEnum: true },
  Number: { type: "number", xFormat: null, hasEnum: false },
  "Multi-Select": { type: "array", xFormat: null, hasEnum: true },
};

const X_SOURCES_BY_TYPE = {
  "Date/Time": ["START_TIME", "END_TIME"],
  Number: [
    "ACRES_SINCE_LAST_RESET",
    "ACRES_SINCE_START_TIME",
    "TOTAL_MIX_APPLIED",
  ],
  "Multi-Select": [
    "WEEDS_SINCE_START_TIME",
    "CHEMICAL_LOAD_OZ_SINCE_START_TIME",
    "CHEMICAL_DATA_SINCE_START_TIME",
  ],
};

export default class Schemabuilder extends Controller {
  static targets = [
    "title",
    "formatId",
    "fieldList",
    "addFieldDialog",
    "fieldName",
    "fieldType",
    "autoFill",
    "enumValues",
    "enumContainer",
    "autoFillContainer",
    "output",
    "copyBtn",
  ];

  connect() {
    this.fields = [];
    this.generateSchema();
  }

  openAddField() {
    this.fieldNameTarget.value = "";
    this.fieldTypeTarget.value = "Text";
    this.enumValuesTarget.value = "";
    this.typeChanged();
    this.addFieldDialogTarget.showModal();
  }

  closeAddField() {
    this.addFieldDialogTarget.close();
  }

  backdropClick(event) {
    if (event.target === this.addFieldDialogTarget) {
      this.closeAddField();
    }
  }

  typeChanged() {
    const userType = this.fieldTypeTarget.value;
    const config = FIELD_TYPES[userType];

    // Show/hide enum input
    if (config.hasEnum) {
      this.enumContainerTarget.classList.remove("hidden");
    } else {
      this.enumContainerTarget.classList.add("hidden");
    }

    // Show/hide and populate auto-fill dropdown
    const sources = X_SOURCES_BY_TYPE[userType];
    if (sources) {
      this.autoFillContainerTarget.classList.remove("hidden");
      this.autoFillTarget.innerHTML = '<option value="">None</option>';
      sources.forEach((src) => {
        const opt = document.createElement("option");
        opt.value = src;
        opt.textContent = src;
        this.autoFillTarget.appendChild(opt);
      });
    } else {
      this.autoFillContainerTarget.classList.add("hidden");
      this.autoFillTarget.innerHTML = '<option value="">None</option>';
    }
  }

  addField() {
    const name = this.fieldNameTarget.value.trim();
    if (!name) {
      alert("Field name is required.");
      return;
    }

    if (this.fields.some((f) => f.name === name)) {
      alert("A field with this name already exists.");
      return;
    }

    const userType = this.fieldTypeTarget.value;
    const config = FIELD_TYPES[userType];
    const xSource = this.autoFillTarget.value || null;

    let enumValues = [];
    if (config.hasEnum) {
      enumValues = this.enumValuesTarget.value
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v);
    }

    this.fields.push({
      name,
      userType,
      type: config.type,
      xFormat: config.xFormat,
      xSource,
      enumValues,
      required: false,
    });

    this.closeAddField();
    this.renderFields();
    this.generateSchema();
  }

  removeField(event) {
    const index = parseInt(event.currentTarget.dataset.index);
    this.fields.splice(index, 1);
    this.renderFields();
    this.generateSchema();
  }

  toggleRequired(event) {
    const index = parseInt(event.currentTarget.dataset.index);
    this.fields[index].required = event.currentTarget.checked;
    this.generateSchema();
  }

  moveFieldUp(event) {
    const index = parseInt(event.currentTarget.dataset.index);
    if (index === 0) return;
    [this.fields[index - 1], this.fields[index]] = [
      this.fields[index],
      this.fields[index - 1],
    ];
    this.renderFields();
    this.generateSchema();
  }

  moveFieldDown(event) {
    const index = parseInt(event.currentTarget.dataset.index);
    if (index >= this.fields.length - 1) return;
    [this.fields[index], this.fields[index + 1]] = [
      this.fields[index + 1],
      this.fields[index],
    ];
    this.renderFields();
    this.generateSchema();
  }

  renderFields() {
    const container = this.fieldListTarget;
    container.innerHTML = "";

    if (this.fields.length === 0) {
      container.innerHTML =
        '<p class="text-sm text-gray-500">No fields added yet. Click "+ Add Field" to get started.</p>';
      return;
    }

    this.fields.forEach((field, index) => {
      const row = document.createElement("div");
      row.className =
        "flex items-center gap-3 p-3 bg-white rounded-md border border-gray-200";

      const nameSpan = document.createElement("span");
      nameSpan.className = "font-medium flex-grow";
      nameSpan.textContent = field.name;
      row.appendChild(nameSpan);

      const typeBadge = document.createElement("span");
      typeBadge.className =
        "text-xs bg-gray-100 rounded px-2 py-0.5 text-gray-600";
      typeBadge.textContent = field.userType;
      row.appendChild(typeBadge);

      if (field.xSource) {
        const sourceBadge = document.createElement("span");
        sourceBadge.className =
          "text-xs bg-blue-100 rounded px-2 py-0.5 text-blue-700";
        sourceBadge.textContent = field.xSource;
        row.appendChild(sourceBadge);
      }

      const reqLabel = document.createElement("label");
      reqLabel.className = "flex items-center gap-1 text-sm";
      const reqCheckbox = document.createElement("input");
      reqCheckbox.type = "checkbox";
      reqCheckbox.checked = field.required;
      reqCheckbox.dataset.index = index;
      reqCheckbox.dataset.action = "change->schemabuilder#toggleRequired";
      reqLabel.appendChild(reqCheckbox);
      reqLabel.appendChild(document.createTextNode(" Required"));
      row.appendChild(reqLabel);

      const upBtn = document.createElement("button");
      upBtn.type = "button";
      upBtn.className =
        "text-gray-400 hover:text-gray-600 text-sm" +
        (index === 0 ? " invisible" : "");
      upBtn.textContent = "\u2191";
      upBtn.dataset.index = index;
      upBtn.dataset.action = "click->schemabuilder#moveFieldUp";
      row.appendChild(upBtn);

      const downBtn = document.createElement("button");
      downBtn.type = "button";
      downBtn.className =
        "text-gray-400 hover:text-gray-600 text-sm" +
        (index === this.fields.length - 1 ? " invisible" : "");
      downBtn.textContent = "\u2193";
      downBtn.dataset.index = index;
      downBtn.dataset.action = "click->schemabuilder#moveFieldDown";
      row.appendChild(downBtn);

      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "text-red-500 hover:text-red-700 text-sm";
      removeBtn.textContent = "\u00d7";
      removeBtn.dataset.index = index;
      removeBtn.dataset.action = "click->schemabuilder#removeField";
      row.appendChild(removeBtn);

      container.appendChild(row);
    });
  }

  generateSchema() {
    const schema = {
      type: "object",
      title: this.titleTarget.value || "Untitled",
      "X-Order": [],
      required: [],
      properties: {},
    };

    const formatId = this.formatIdTarget.value.trim();
    if (formatId) {
      schema["X-Format-ID"] = formatId;
    }

    for (const field of this.fields) {
      schema["X-Order"].push(field.name);
      if (field.required) schema.required.push(field.name);

      const prop = {
        type: field.type,
        title: field.name,
      };

      if (field.xFormat) {
        prop["X-Format"] = field.xFormat;
      }

      if (field.xSource) {
        prop["X-Source"] = field.xSource;
      }

      if (field.enumValues && field.enumValues.length > 0) {
        if (field.type === "array") {
          prop.items = { type: "string", enum: field.enumValues };
        } else {
          prop.enum = field.enumValues;
        }
      }

      if (
        field.type === "array" &&
        field.xSource === "CHEMICAL_DATA_SINCE_START_TIME"
      ) {
        prop.items = { $ref: "#/definitions/chemicalData" };
      }

      schema.properties[field.name] = prop;
    }

    // Add chemicalData definition if needed
    const needsChemDef = this.fields.some(
      (f) => f.xSource === "CHEMICAL_DATA_SINCE_START_TIME"
    );
    if (needsChemDef) {
      schema.definitions = {
        chemicalData: {
          type: "object",
          required: [
            "chemical_supplier_id",
            "chemical_supplier_name",
            "chemical_load_id",
            "ounces",
            "label",
          ],
          properties: {
            acres: { type: "number" },
            label: { type: "string", "X-Source": "LABEL" },
            ounces: { type: "number" },
            chemical_load_id: { type: "string" },
            chemical_supplier_id: { type: "string" },
            chemical_supplier_name: { type: "string" },
          },
        },
      };
    }

    this.outputTarget.value = JSON.stringify(schema, null, 2);
  }

  copyJson() {
    navigator.clipboard.writeText(this.outputTarget.value).then(() => {
      const btn = this.copyBtnTarget;
      const original = btn.textContent;
      btn.textContent = "Copied!";
      setTimeout(() => {
        btn.textContent = original;
      }, 2000);
    });
  }
}
